import { api } from "@breakout/backend/convex/_generated/api"
import { convexQuery } from "@convex-dev/react-query"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { zodSearchValidator } from "@tanstack/router-zod-adapter"
import { ArrowLeft } from "lucide-react"
import { useCallback } from "react"
import { z } from "zod"
import { TrendingTweets } from "../components/home/TrendingTweets"
import { PageContainer } from "../components/layout/PageContainer"
import { TweetSortToggle } from "../components/market/TweetSortToggle"
import { XLogo } from "../components/ui/XLogo"

const timelineSearchSchema = z.object({
	sort: z.enum(["engagement", "recent"]).optional().catch(undefined),
})

export const Route = createFileRoute("/timeline")({
	component: TimelinePage,
	validateSearch: zodSearchValidator(timelineSearchSchema),
})

function TimelinePage() {
	const { sort } = Route.useSearch()
	const navigate = useNavigate()
	const sortBy = sort ?? "recent"

	const { data, isLoading } = useQuery(convexQuery(api.public.home.getWeekViewData, {}))

	const handleSortChange = useCallback(
		(newSort: "engagement" | "recent") => {
			void navigate({
				to: "/timeline",
				search: newSort === "recent" ? {} : { sort: newSort },
				replace: true,
			})
		},
		[navigate],
	)

	// Show "no active week" only when query resolved with no data
	const showNoWeek = !isLoading && !data

	return (
		<div className="bg-bg-primary text-text-primary">
			<PageContainer className="pt-5 pb-8 sm:pt-8">
				<div className="mb-5">
					<div className="flex items-center gap-2 sm:gap-2.5">
						<Link
							aria-label="Back to markets"
							className="inline-flex items-center justify-center rounded-lg p-1 text-text-muted transition-colors hover:text-text-primary"
							to="/"
						>
							<ArrowLeft aria-hidden="true" className="size-5 sm:size-6" />
						</Link>
						<XLogo aria-hidden="true" className="size-5 text-text-primary sm:size-6" />
						<h1 className="font-bold text-lg text-text-primary sm:text-xl">CT Bangers</h1>
					</div>
					<p className="mt-1.5 pl-8 text-sm text-text-muted sm:pl-9">
						Top posts moving mindshare and markets.
					</p>
				</div>

				{/* Sort toggle always visible — avoids layout shift between loading states */}
				<div className="mb-4">
					<TweetSortToggle onSortChange={handleSortChange} sortBy={sortBy} />
				</div>

				{showNoWeek ? (
					<div className="rounded-2xl border border-border border-dashed bg-bg-card py-16 text-center shadow-sm">
						<p className="font-medium text-text-muted">No active week</p>
						<p className="mt-2 text-sm text-text-muted">
							Check back later for the latest from top creators.
						</p>
					</div>
				) : (
					<TrendingTweets
						onSortChange={handleSortChange}
						sinceMs={data ? data.week.openTimeSec * 1000 : null}
						sortBy={sortBy}
					/>
				)}
			</PageContainer>
		</div>
	)
}
